import { createContext, useContext, useState, useEffect } from 'react';
import { collection, getDocs, onSnapshot, doc, increment, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

const DataContext = createContext();

export function DataProvider({ children }) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [messages, setMessages] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shippingZones, setShippingZones] = useState([]);
  const [analytics, setAnalytics] = useState({
    totalVisits: 0,
    pageViews: {},
    productViews: {}
  });

  useEffect(() => {
    const unsubscribeProducts = onSnapshot(collection(db, 'products'), (snapshot) => {
      const productsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProducts(productsData);
    });

    const unsubscribeCategories = onSnapshot(collection(db, 'categories'), (snapshot) => {
      const categoriesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCategories(categoriesData);
    });

    const unsubscribeMessages = onSnapshot(collection(db, 'messages'), (snapshot) => {
      const messagesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMessages(messagesData);
    });

    const unsubscribeOrders = onSnapshot(collection(db, 'orders'), (snapshot) => {
      const ordersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setOrders(ordersData);
    });

    const unsubscribeShipping = onSnapshot(collection(db, 'shippingZones'), (snapshot) => {
      const shippingData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setShippingZones(shippingData);
      setLoading(false);
    });

    const unsubscribeAnalytics = onSnapshot(
      doc(db, 'analytics', 'pageViews'),
      (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          setAnalytics(prev => ({
            ...prev,
            totalVisits: data.totalVisits || 0,
            pageViews: data.pages || {}
          }));
        }
      }
    );

    const unsubscribeProductViews = onSnapshot(
      doc(db, 'analytics', 'productViews'),
      (doc) => {
        if (doc.exists()) {
          setAnalytics(prev => ({
            ...prev,
            productViews: doc.data().products || {}
          }));
        }
      }
    );

    return () => {
      unsubscribeProducts();
      unsubscribeCategories();
      unsubscribeMessages();
      unsubscribeOrders();
      unsubscribeShipping();
      unsubscribeAnalytics();
      unsubscribeProductViews();
    };
  }, []);

  const refreshData = async () => {
    setLoading(true);
    try {
      const [productsSnap, categoriesSnap, messagesSnap, ordersSnap] = await Promise.all([
        getDocs(collection(db, 'products')),
        getDocs(collection(db, 'categories')),
        getDocs(collection(db, 'messages')),
        getDocs(collection(db, 'orders'))
      ]);

      setProducts(productsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setCategories(categoriesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setMessages(messagesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setOrders(ordersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Erreur lors du rafraîchissement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  const incrementPageView = async (pagePath) => {
    try {
      const analyticsRef = doc(db, 'analytics', 'pageViews');
      const analyticsDoc = await getDoc(analyticsRef);
      
      if (!analyticsDoc.exists()) {
        await setDoc(analyticsRef, {
          totalVisits: 1,
          pages: {
            [pagePath]: 1
          }
        });
      } else {
        await updateDoc(analyticsRef, {
          totalVisits: increment(1),
          [`pages.${pagePath}`]: increment(1)
        });
      }
    } catch (error) {
      console.error('Erreur lors du suivi de page:', error);
    }
  };

  const incrementProductView = async (productId) => {
    try {
      const analyticsRef = doc(db, 'analytics', 'productViews');
      const analyticsDoc = await getDoc(analyticsRef);
      
      if (!analyticsDoc.exists()) {
        await setDoc(analyticsRef, {
          products: {
            [productId]: 1
          }
        });
      } else {
        await updateDoc(analyticsRef, {
          [`products.${productId}`]: increment(1)
        });
      }
    } catch (error) {
      console.error('Erreur lors du suivi de produit:', error);
    }
  };

  return (
    <DataContext.Provider value={{
      products,
      categories,
      messages,
      orders,
      loading,
      refreshData,
      shippingZones,
      analytics,
      incrementPageView,
      incrementProductView
    }}>
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData doit être utilisé à l\'intérieur d\'un DataProvider');
  }
  return context;
} 